import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  fetchNotifications,
  selectActiveNotificationTab,
  selectNotifications,
  selectNotificationsLoading,
  selectUnreadCount,
  setActiveTab
} from 'lib/features/notification/notificationSlice';
import { NotificationType } from 'lib/features/notification/types';
import { selectUser } from 'lib/features/user/userSlice';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface NotificationPopupProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

interface GroupedNotifications {
  today: any[];
  yesterday: any[];
  last7days: any[];
  last30days: any[];
}

const groupNotificationsByDate = (
  notifications: any[]
): GroupedNotifications => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const grouped: GroupedNotifications = {
    today: [],
    yesterday: [],
    last7days: [],
    last30days: [],
  };

  notifications.forEach((notif) => {
    const notifDate = new Date(notif.createdAt);
    const notifDateOnly = new Date(
      notifDate.getFullYear(),
      notifDate.getMonth(),
      notifDate.getDate()
    );

    if (notifDateOnly.getTime() === today.getTime()) {
      grouped.today.push(notif);
    } else if (notifDateOnly.getTime() === yesterday.getTime()) {
      grouped.yesterday.push(notif);
    } else if (notifDateOnly.getTime() > sevenDaysAgo.getTime()) {
      grouped.last7days.push(notif);
    } else if (notifDateOnly.getTime() > thirtyDaysAgo.getTime()) {
      grouped.last30days.push(notif);
    }
  });

  return grouped;
};

export const NotificationsPopup: React.FC<NotificationPopupProps> = ({
  anchorEl,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);

  const allNotifications = useAppSelector(selectNotifications);
  const loading = useAppSelector(selectNotificationsLoading);
  const unreadCount = useAppSelector(selectUnreadCount);
  const activeTab = useAppSelector(selectActiveNotificationTab);

  const tabs = ['All', 'Bookings', 'Referrals', 'Messages'];

  const filteredNotifications =
    activeTab === 'All'
      ? allNotifications
      : allNotifications.filter((n) => n.type === activeTab);

  const groupedNotifications = groupNotificationsByDate(filteredNotifications);

  useEffect(() => {
    if (anchorEl && user?.id) {
      dispatch(fetchNotifications(user.id) as any);
    }
  }, [anchorEl, user?.id, dispatch]);

  const handleTabChange = (tab: string) => {
    dispatch(setActiveTab(tab));
  };

  // const handleNotificationClick = async (notification: any) => {
  //   try {
  //     dispatch(
  //       markNotificationAsRead({
  //         notificationId: notification.id,
  //         userId: user?.id!,
  //         ids: notification.ids,
  //       }) as any
  //     );

  //     switch (notification.type) {
  //       case NotificationType.Messages:
  //         if (notification.channel) {
  //           navigate(`/messages/${notification.channel}`);
  //         }
  //         break;
  //       case NotificationType.Referrals:
  //         navigate('/bookings?tab=referrals');
  //         break;
  //       case NotificationType.Bookings:
  //         navigate(`/booking-details/${notification.bookingId}`);
  //         break;
  //       case 'Booking Request':
  //         navigate('/bookings?tab=requests');
  //         break;
  //       default:
  //         break;
  //     }

  //     onClose();
  //   } catch (error) {
  //     console.error('Error handling notification:', error);
  //   }
  // };

  const renderNotificationSection = (title: string, items: any[]) => {
    if (items.length === 0) return null;

    return (
      <Box key={title} sx={{ mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '20px',
            color: '#333',
            px: 2,
            py: 1,
            textTransform: 'capitalize',
          }}
        >
          {title}
        </Typography>
        <List sx={{ p: 0 }}>
          {items.map((notification) => (
            <ListItem
              key={notification.id}
              disablePadding
              sx={{
                backgroundColor: !notification.isRead
                  ? '#E8F5E9'
                  : 'transparent',
                borderLeft: !notification.isRead
                  ? '4px solid #4CAF50'
                  : '4px solid transparent',
                '&:hover': {
                  backgroundColor: !notification.isRead
                    ? '#D0EED5'
                    : '#D0EED5',
                },
                transition: 'background-color 0.2s',
              }}
            >
              <ListItemButton
                // onClick={() => handleNotificationClick(notification)}
                sx={{
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
                  <ListItemAvatar sx={{ minWidth: 60, mr: 1.5 }}>
                    <Avatar
                      alt={notification.sender?.name}
                      src={
                        notification.sender?.avatar
                          ? `https://applicationimages.s3.amazonaws.com/${notification.sender.avatar}`
                          : ''
                      }
                      sx={{
                        width: 60,
                        height: 60,
                        border: !notification.isRead
                          ? '2px solid #4CAF50'
                          : 'none',
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: !notification.isRead ? 700 : 600,
                          color: '#000',
                          mb: 0.3,
                          lineHeight: 1.3,
                          mt: -1
                        }}
                      >
                        {notification.sender?.name || 'Unknown'}{' '}
                        {getNotificationTitle(notification)}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666',
                          display: 'block',
                          mt: 0.3,
                          lineHeight: 1.4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'normal',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {notification.message}
                      </Typography>
                    }
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999',
                    fontSize: '12px',
                    ml: 1,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {notification.createdAtFormatted}
                </Typography>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(anchorEl)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 420,
          backgroundColor: '#fff',
          borderRadius: 0,
          boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)',
          height: '100vh',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': {
            background: '#ccc',
            borderRadius: '3px',
          },
        }}
      >
        {/* HEADER */}
        <Box sx={{ p: 2, mt:5, borderBottom: '1px solid #eee' }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: '26px', mb: 1 }}
          >
            Notifications
          </Typography>

          {/* Tabs */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {tabs.map((tab) => (
              <Chip
                key={tab}
                label={tab}
                onClick={() => handleTabChange(tab)}
                variant={activeTab === tab ? 'filled' : 'outlined'}
                size="small"
                sx={{
                  fontWeight: activeTab === tab ? 700 : 500,
                  backgroundColor:
                    activeTab === tab ? '#4CAF50' : 'transparent',
                  color: activeTab === tab ? '#fff' : '#000',
                  borderColor: '#ddd',
                  '&:hover': {
                    backgroundColor:
                      activeTab === tab ? '#45a049' : '#f0f0f0',
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* CONTENT */}
        <Box sx={{ flex: 1, p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={36} />
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography color="textSecondary" variant="body2">
                No notifications available
              </Typography>
            </Box>
          ) : (
            <>
              {renderNotificationSection('Today', groupedNotifications.today)}
              {renderNotificationSection(
                'Yesterday',
                groupedNotifications.yesterday
              )}
              {renderNotificationSection(
                'Last 7 days',
                groupedNotifications.last7days
              )}
              {renderNotificationSection(
                'Last 30 days',
                groupedNotifications.last30days
              )}
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

const getNotificationTitle = (notification: any): string => {
  switch (notification.type) {
    case NotificationType.Messages:
      return 'sent you a Message';
    case NotificationType.Bookings:
      return 'New Booking!';
    case NotificationType.Referrals:
      return 'New Referral!';
    case 'Booking Request':
      return 'New Booking Request';
    default:
      return '';
  }
};
